import { Component, inject, OnInit, Inject, OnDestroy, AfterViewInit, computed, signal, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { XButtonComponent } from 'src/app/shared/x-kit/components/x-button/x-button.component';
import { PopoverLayoutComponent } from 'src/app/map-modules/external-entities/shared/popover-layout/popover-layout.component';
import { Announcement, AnnouncementsApiService } from 'src/app/shared/services/api/announcements-api.service';
import { MediaQueryService } from 'src/app/shared/services/media-query.service';
import { catchError, retry, throwError, takeUntil, Subject } from 'rxjs';
import { captureException } from '@sentry/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface AnnouncementModalData {
  announcement: Announcement;
}

@Component({
  selector: 'app-announcement-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslateModule,
    SvgIconComponent,
    XButtonComponent,
    PopoverLayoutComponent,
    DatePipe,
  ],
  templateUrl: './announcement-modal.component.html',
  styleUrls: ['./announcement-modal.component.less'],
})
export class AnnouncementModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentContainer', { static: false }) contentContainer!: ElementRef<HTMLDivElement>;

  private dialogRef = inject(MatDialogRef<AnnouncementModalComponent>);
  private announcementsApiService = inject(AnnouncementsApiService);
  private mediaQueryService = inject(MediaQueryService);
  private sanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();

  public announcement: Announcement;
  public isReadSent = false;
  public isLoading = false;
  public isMobile = signal(false);
  public collapsedSections = signal<Set<string>>(new Set());
  public sanitizedContent: SafeHtml | null = null;

  // Computed properties
  public currentLayout = computed(() => this.announcement?.layout || 'long');
  public showTryNow = computed(() => !!this.announcement?.actionUrl);
  public hasChangelogUrl = computed(() => !!this.announcement?.changelogUrl);

  constructor(@Inject(MAT_DIALOG_DATA) public data: AnnouncementModalData) {
    this.announcement = data.announcement;
    if (this.announcement?.content) {
      this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(this.announcement.content);
    }
  }

  ngOnInit() {
    // Подписка на изменения мобильного режима
    this.mediaQueryService.mobileMediaQuery
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMobile) => {
        this.isMobile.set(isMobile);
      });

    // Перехватываем закрытие через backdrop click
    this.dialogRef.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // При клике на backdrop отправляем запрос на прочтение, если еще не отправлен
        if (this.announcement && !this.isReadSent && !this.isLoading) {
          this.markAsRead();
        } else if (!this.isLoading) {
          // Если запрос уже отправлен и не выполняется, просто закрываем
          this.dialogRef.close();
        }
        // Если isLoading === true, ничего не делаем - markAsRead сам закроет диалог
      });

    // Перехватываем закрытие через ESC
    this.dialogRef.keydownEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          // При нажатии ESC отправляем запрос на прочтение, если еще не отправлен
          if (this.announcement && !this.isReadSent && !this.isLoading) {
            event.preventDefault();
            this.markAsRead();
          } else if (!this.isLoading) {
            // Если запрос уже отправлен и не выполняется, разрешаем закрытие
            // ESC закроет диалог автоматически
          } else {
            // Если запрос выполняется, предотвращаем закрытие
            event.preventDefault();
          }
        }
      });
  }

  ngAfterViewInit() {
    // Оживляем HTML контент после рендеринга
    setTimeout(() => {
      this.attachEventListeners();
    }, 0);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Привязываем обработчики событий к элементам внутри HTML контента
   */
  private attachEventListeners() {
    if (!this.contentContainer?.nativeElement) {
      return;
    }

    const container = this.contentContainer.nativeElement;

    // Обработка collapsible секций
    const collapsibleHeaders = container.querySelectorAll('[data-collapsible-section]');
    collapsibleHeaders.forEach((header) => {
      const sectionId = header.getAttribute('data-collapsible-section');
      if (sectionId) {
        header.addEventListener('click', () => this.toggleCollapsibleSection(sectionId, header));
        // Инициализируем состояние (по умолчанию все секции развернуты)
        this.updateCollapsibleSectionState(sectionId, header, false);
      }
    });

    // Обработка кнопок с data-action (включая кнопки внутри контента для двойной новости)
    const actionButtons = container.querySelectorAll('[data-action]');
    actionButtons.forEach((button) => {
      const action = button.getAttribute('data-action');
      const actionUrl = button.getAttribute('data-action-url');
      if (action) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAction(action, button, actionUrl);
        });
      }
    });

    // Обработка ссылок с data-external
    const externalLinks = container.querySelectorAll('a[data-external]');
    externalLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = (link as HTMLAnchorElement).href;
        if (href) {
          window.open(href, '_blank');
        }
      });
    });
  }

  /**
   * Переключение состояния collapsible секции
   */
  private toggleCollapsibleSection(sectionId: string, headerElement: Element) {
    const isCollapsed = this.collapsedSections().has(sectionId);
    const collapsed = new Set(this.collapsedSections());
    
    if (isCollapsed) {
      collapsed.delete(sectionId);
    } else {
      collapsed.add(sectionId);
    }
    
    this.collapsedSections.set(collapsed);
    this.updateCollapsibleSectionState(sectionId, headerElement, !isCollapsed);
  }

  /**
   * Обновление визуального состояния collapsible секции
   */
  private updateCollapsibleSectionState(sectionId: string, headerElement: Element, willBeCollapsed: boolean) {
    const contentElement = headerElement.nextElementSibling;
    const chevronElement = headerElement.querySelector('.section-chevron');
    
    if (contentElement && contentElement.classList.contains('collapsible-section-content')) {
      if (willBeCollapsed) {
        contentElement.classList.add('collapsed');
        // Обновляем SVG иконку chevron
        if (chevronElement) {
          const svg = chevronElement.querySelector('svg');
          if (svg) {
            // Меняем направление chevron на right
            const path = svg.querySelector('path');
            if (path) {
              // Это упрощенная версия - в реальности нужно использовать правильный SVG
              chevronElement.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            }
          }
        }
      } else {
        contentElement.classList.remove('collapsed');
        // Обновляем SVG иконку chevron
        if (chevronElement) {
          const svg = chevronElement.querySelector('svg');
          if (svg) {
            // Меняем направление chevron на down
            chevronElement.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          }
        }
      }
    }
  }

  /**
   * Обработка действий из HTML контента
   */
  private handleAction(action: string, element: Element, actionUrl?: string | null) {
    switch (action) {
      case 'try-now':
        // Если есть data-action-url, открываем его, иначе используем основной actionUrl
        if (actionUrl) {
          window.open(actionUrl, '_blank');
          this.markAsRead();
        } else {
          this.onTryNow();
        }
        break;
      case 'understand':
        this.onUnderstand();
        break;
      case 'later':
        this.onLater();
        break;
      default:
        console.warn('[Announcement] Unknown action:', action);
    }
  }

  onLater() {
    // При нажатии "Later" просто закрываем без вызова /read
    if (!this.isLoading) {
      this.dialogRef.close();
    }
  }

  onClose() {
    // При закрытии через крестик отправляем запрос на прочтение, если еще не отправлен
    if (this.announcement && !this.isReadSent && !this.isLoading) {
      this.markAsRead();
    } else if (!this.isLoading) {
      // Если запрос уже отправлен и не выполняется, просто закрываем
      this.dialogRef.close();
    }
    // Если isLoading === true, ничего не делаем - markAsRead сам закроет диалог
  }

  onUnderstand() {
    if (!this.announcement || this.isReadSent || this.isLoading) {
      this.dialogRef.close();
      return;
    }

    // При нажатии "Understand" отправляем запрос
    this.markAsRead();
  }

  onTryNow() {
    if (!this.announcement || this.isReadSent || this.isLoading) {
      this.dialogRef.close();
      return;
    }

    // Выполняем действие в зависимости от actionType
    if (this.announcement.actionType === 'link' && this.announcement.actionUrl) {
      window.open(this.announcement.actionUrl, '_blank');
    }

    // Закрываем модалку и отправляем запрос на прочтение
    this.markAsRead();
  }

  onChangelogClick(event: Event) {
    if (this.announcement.changelogUrl) {
      event.preventDefault();
      window.open(this.announcement.changelogUrl, '_blank');
    }
  }

  toggleSection(sectionTitle: string) {
    const collapsed = new Set(this.collapsedSections());
    if (collapsed.has(sectionTitle)) {
      collapsed.delete(sectionTitle);
    } else {
      collapsed.add(sectionTitle);
    }
    this.collapsedSections.set(collapsed);
  }

  isSectionCollapsed(sectionTitle: string): boolean {
    return this.collapsedSections().has(sectionTitle);
  }

  private markAsRead() {
    if (!this.announcement || this.isReadSent || this.isLoading) {
      return;
    }

    this.isReadSent = true;
    this.isLoading = true;

    this.announcementsApiService
      .markAsRead(this.announcement.id)
      .pipe(
        retry(1), // Один ретрай при ошибке
        catchError((error) => {
          console.error('[Announcement] Failed to mark as read:', error);
          captureException(error);
          // В случае ошибки просто закрываем модалку
          // Анонс может показаться снова в будущем - это допустимо
          return throwError(() => error);
        })
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.dialogRef.close();
        },
        error: () => {
          this.isLoading = false;
          this.dialogRef.close();
        },
      });
  }
}
